import { CountResponse } from './count';
import { locationFilters, resetSearchForm } from './filters';
import NumberSpinner from '../numberspinner';
import { OfferType, OfferTypeForHoliday, OfferTypeForRent, OfferTypeForSale } from './offer-types';
import { countFilters } from './filter-count';
import { getOfferTypeForForm, syncPropertySearchForms, updateFilterCountBadges, updatePricesForOfferType } from './search-filters';
import { newState } from '../routing/history';
import { closeAllUtilityPopups, closeSideNav, openUtilityPopup } from '../popups/utils';
import { getHTML, getUtilityPopupContainer } from '../popups/utility-popups';

const locationDestination = 'location';

type Destination = OfferType | 'location';

class LocationSearchForm {
    _form: HTMLFormElement;
    _popupForm: HTMLFormElement;
    _keepCount: boolean;
    _keepCountEndpoint: URL;
    _resetBtns: NodeListOf<HTMLButtonElement>;
    _primaryResetBtn: HTMLButtonElement;
    _submitBtns: NodeListOf<HTMLButtonElement>;
    _locationSubmitBtns: NodeListOf<HTMLButtonElement>;
    _propertySubmitBtns: NodeListOf<HTMLButtonElement>;
    _simpleFilterBtns: NodeListOf<HTMLButtonElement>;
    _simpleFilterFieldsets: NodeListOf<HTMLFieldSetElement>;
    _countAborter: AbortController | null = null;
    _countTimeout: number | null = null;
    _currentCountEventTracker: string = '';
    _propertyCounters: Array<NumberSpinner> = [];
    _locationCounters: Array<NumberSpinner> = [];
    _geoSimpleSelect: HTMLSelectElement | null = null;
    _switchingPopupForm: HTMLFormElement | null = null;
    _switchingPopup: HTMLElement | null = null;

    constructor( form: HTMLFormElement ) {
        this._form = form;

        if ( this._form.dataset.keepCount && this._form.dataset.keepCount === 'true' && this._form.dataset.keepCountEndpoint ) {
            this._keepCount = true;
            this._keepCountEndpoint = new URL( window.location.origin + this._form.dataset.keepCountEndpoint );

            this._form.querySelectorAll( '.propertycount' ).forEach( ( el: HTMLElement ) => this._propertyCounters = [ ...this._propertyCounters, new NumberSpinner( el ) ] );
            this._form.querySelectorAll( '.locationcount' ).forEach( ( el: HTMLElement ) => this._locationCounters = [ ...this._locationCounters, new NumberSpinner( el ) ] );
        }

        this._resetBtns = this._form.querySelectorAll( 'button[type="reset"]' );
        this._primaryResetBtn = this._form.querySelector( '#location-filters-reset' ) as HTMLButtonElement;
        this._submitBtns = this._form.querySelectorAll( 'button[type="submit"]' );
        this._locationSubmitBtns = this._form.querySelectorAll( '.locationsearchcounter__btn--locations' );
        this._propertySubmitBtns = this._form.querySelectorAll( '.locationsearchcounter__btn--property' );
        this._simpleFilterBtns = this._form.querySelectorAll( '.simplelocationfilter' );
        this._simpleFilterFieldsets = this._form.querySelectorAll( '.simplelocationfilter > fieldset' );
        this._popupForm = document.querySelector( '#property-search-form-popup' ) as HTMLFormElement;
        this._geoSimpleSelect = this._form.querySelector( 'select[name=geoSimpleSelect]' );

        this._popupForm.dataset.propertyCount = this._form.dataset.propertyCount;
        this._popupForm.dataset.locationCount = this._form.dataset.locationCount;

        this._doFilterCountBadgeUpdate();

        this._initEventListeners();
    }

    _initEventListeners(): void {
        this._resetBtns.forEach( ( btn ) => btn.addEventListener( 'click', this._onReset.bind( this ) ) );
        this._submitBtns.forEach( ( btn ) => btn.addEventListener( 'click', this._onSubmit.bind( this ) ) );
        this._simpleFilterBtns.forEach( ( btn ) => btn.addEventListener( 'click', this._onSimpleFilterClick.bind( this ) ) );

        this._form.querySelectorAll( '[name=offer]' ).forEach( ( element: HTMLInputElement | HTMLSelectElement ) => {
            element.addEventListener( 'change', this._onOfferTypeChange.bind( this ) );
        } );

        this._form.querySelectorAll( '.locationsearchfiltercategory__main' ).forEach( ( element: HTMLElement ) => {
            element.addEventListener( 'collapse:expand', this._onFilterSectionShow.bind( this ) );
            element.addEventListener( 'collapse:collapsed', this._onFilterSectionHide.bind( this ) );
        } );

        if ( this._geoSimpleSelect ) {
            this._geoSimpleSelect.addEventListener( 'change', this._onGeoSimpleSelectChange.bind( this ) );
        }

        this._form.addEventListener( 'twc:typeahead:value-selected', this._onTypeaheadSelect.bind( this ) );
        this._form.addEventListener( 'twc:typeahead-deselect', this._onChange.bind( this ) );
        this._form.addEventListener( 'twc:typeahead:value-selected', this._onChange.bind( this ) );
        this._form.addEventListener( 'change', this._onChange.bind( this ) );

        this._initModeSwitchPopup();
    }

    _initModeSwitchPopup(): void {
        this._switchingPopupForm = document.querySelector( '#fyp-mode-switch-popup form' );
        this._switchingPopup = document.querySelector( '#fyp-mode-switch-popup' );

        if ( this._switchingPopup && this._switchingPopupForm ) {
            const closeBtn = document.querySelector( '#fyp-mode-switch-popup .popup__close' );

            if ( closeBtn ) {
                closeBtn.addEventListener( 'click', () => closeAllUtilityPopups() );
            }

            this._switchingPopupForm.addEventListener( 'submit', this._onSwitchPopupSubmit.bind( this ) );
        }
    }

    async _onReset( event: Event ): Promise<void> {
        if ( !event.target ) {
            return;
        }

        event.preventDefault();

        const target = event.target as HTMLButtonElement;

        let resetContainer: HTMLElement = this._form;

        let collapseSections = true;

        if ( target.closest( '.locationsearchfiltercategory' ) !== null ) {
            resetContainer = target.closest( '.locationsearchfiltercategory' ) as HTMLElement;
            collapseSections = false;
        }

        const typeaheadInput = resetContainer.querySelector( '[name="locationId"]' );

        if ( typeaheadInput ) {
            typeaheadInput.dispatchEvent( new CustomEvent( 'twc:typeahead:set-value', {
                bubbles: true,
                detail: {
                    label: '',
                    state: {
                        value: '',
                    },
                },
            } ) );
        }

        const orderByInput: HTMLInputElement | null = resetContainer.querySelector( 'input[name=order]' );

        if ( orderByInput ) {
            orderByInput.value = '';
        }

        const locationFilterNames = locationFilters.all();

        const propertyFilterNames: Array<string> = Array.from( resetContainer.querySelectorAll( 'input,select' ) ).map( ( element: HTMLInputElement | HTMLSelectElement ) => {
            if ( !locationFilterNames.includes( element.name ) ) {
                return element.name;
            }
            return '';
        } ).filter( ( inputName: string ) => inputName.length > 0 ) as Array<string>;

        resetSearchForm( resetContainer, propertyFilterNames );

        resetContainer.querySelectorAll( '.simplelocationfilter' ).forEach( ( simpleFilterBtn: HTMLButtonElement ) => {
            simpleFilterBtn.classList.remove( 'simplelocationfilter--enabled' );

            const innerFieldset = simpleFilterBtn.querySelector( 'fieldset' );

            if ( innerFieldset ) {
                innerFieldset.disabled = true;
            }

            const innerSwitchInput = simpleFilterBtn.querySelector<HTMLInputElement>( 'input[type=checkbox]' );

            if ( innerSwitchInput ) {
                innerSwitchInput.checked = false;
            }
        } );

        if ( this._keepCount ) {
            await this._handleCounterUpdate( event );
        }

        if ( collapseSections ) {
            this._form.querySelectorAll( '.locationsearchfiltercategory__btn-minimise' ).forEach( ( minimiseBtn: HTMLButtonElement ) => {
                minimiseBtn.dispatchEvent( new CustomEvent( 'collapse:collapsed', { bubbles: true } ) );
            } );
        }

        this._form.querySelectorAll( '[data-enabled-by]' ).forEach( ( element: HTMLElement ) => {
            element.dispatchEvent( new CustomEvent( 'inputgroup:test', { bubbles: true } ) );
        } );

        this._form.querySelectorAll( '[data-tag-group]' ).forEach( ( group: HTMLElement ) => {
            group.dispatchEvent( new CustomEvent( 'taggroup:collapse', { bubbles: true } ) );
        } );

        this._doFilterCountBadgeUpdate();
    }

    _onTypeaheadSelect( event: CustomEvent ): void {
        const locationID = this._form.querySelector<HTMLInputElement>( 'input[name="locationId"]' );

        if ( event.detail.value && event.detail.value > 0 && locationID ) {
            locationID.value = `location-filters+${event.detail.value}`;
        }

        this._doFilterCountBadgeUpdate();
    }

    _onOfferTypeChange( event: Event ): void {
        const offer = getOfferTypeForForm( this._form );

        updatePricesForOfferType( this._form, offer, false );

        this._form.querySelectorAll( '[data-destination]' ).forEach( ( btn: HTMLButtonElement ) => {
            if ( btn.dataset.destination !== locationDestination ) {
                btn.dataset.destination = offer;
            }
        } );
    }

    _onGeoSimpleSelectChange( event?: Event ): void {
        if ( !this._geoSimpleSelect || !this._geoSimpleSelect.value ) {
            return;
        }

        const fieldset = this._geoSimpleSelect.closest( 'fieldset' );

        if ( !fieldset ) {
            return;
        }

        const tagsInput = fieldset.querySelector<HTMLInputElement>( 'input[name=locationTags]' );
        const locationIDInput = this._form.querySelector<HTMLInputElement>( 'input[name=locationId]' );
        const locationTextInput = this._form.querySelector<HTMLInputElement>( 'input[name=locationFreeText]' );

        if ( !tagsInput || !locationIDInput || !locationTextInput ) {
            return;
        }

        const value = this._geoSimpleSelect.value;

        if ( !/^location-filters/.test( value ) ) {
            tagsInput.value = value;
            tagsInput.disabled = false;
            locationIDInput.value = locationIDInput.dataset.defaultValue || '';
            locationTextInput.value = locationTextInput.dataset.defaultValue || '';
            return;
        }

        const selectedOption = this._geoSimpleSelect.selectedOptions[ 0 ];

        locationIDInput.value = value;
        locationTextInput.value = selectedOption.dataset.displayName as string;
        tagsInput.disabled = true;
        tagsInput.value = '';
    }

    async _onSubmit( event: Event ): Promise<void> {
        event.preventDefault();

        const srcTarget = event.target as HTMLButtonElement;

        if ( !srcTarget ) {
            throw new Error( 'no event target for form submit' );
        }

        const offerTypeInput: HTMLInputElement | null = this._form.querySelector( '[name=offer]' );

        if ( offerTypeInput === null ) {
            throw new Error( 'failed to find offer type input' );
        }

        const orderByInput: HTMLInputElement | null = this._form.querySelector( 'input[name=order]' );

        const hasModeAttr = srcTarget.dataset.mode && srcTarget.dataset.mode.length > 0;

        if ( hasModeAttr ) {
            const fypModeInput = this._form.querySelector<HTMLInputElement>( '[name="fypMode"]' );

            if ( !fypModeInput ) {
                throw new Error( 'failed to find fyo mode input' );
            }

            fypModeInput.value = srcTarget.dataset.mode as string;
        }

        const destination = srcTarget.dataset.destination as Destination | undefined;

        switch ( offerTypeInput.value ) {
        case OfferTypeForSale:
        case OfferTypeForRent:
        case OfferTypeForHoliday:
            if ( orderByInput !== null ) {
                orderByInput.value = '';
            }
            break;
        case locationDestination:
            break;
        default:
            throw new Error( `unknown destination type "${destination}"` );
        }

        const destinationInput: HTMLInputElement | null = this._form.querySelector( '[name=destination]' );

        if ( destinationInput === null ) {
            throw new Error( 'failed to find destination input' );
        }

        if ( destination ) {
            destinationInput.value = destination;
        }

        const filterCompatibilityInput = this._form.querySelector<HTMLInputElement>( 'input[name=fypFilterIsModeInterchangable]' );

        if ( hasModeAttr && filterCompatibilityInput && filterCompatibilityInput.value === 'false' ) {
            if ( !this._switchingPopup ) {
                const popupContainer = getUtilityPopupContainer();
                this._switchingPopup = await getHTML( 'fyp-mode-switch-popup' );
                popupContainer.appendChild( this._switchingPopup );
            }

            if ( !this._switchingPopup ) {
                return;
            }

            this._initModeSwitchPopup();

            await closeSideNav();

            closeAllUtilityPopups( false );

            openUtilityPopup( this._switchingPopup );
            return;
        }

        this._form.submit();
    }

    _onSwitchPopupSubmit( event: Event ): void {
        event.preventDefault();

        const data = new FormData( this._switchingPopupForm as HTMLFormElement );

        const fypModeInput = this._form.querySelector<HTMLInputElement>( '[name="fypMode"]' );

        if ( !fypModeInput ) {
            throw new Error( 'failed to find fyo mode input' );
        }

        fypModeInput.value = data.get( 'fypMode' ) as string;

        this._form.submit();
    }

    _onChange( event: Event ): void {
        syncPropertySearchForms( this._form, this._popupForm );

        this._doFilterCountBadgeUpdate();

        if ( this._keepCount ) {
            this._handleCounterUpdate( event );
        }
    }

    _handleCounterUpdate( event: Event ): void {
        if ( this._countTimeout !== null ) {
            window.clearTimeout( this._countTimeout );
        }

        this._countTimeout = window.setTimeout( () => {
            this._updateCounters( event );
        }, 200 );
    }

    async _updateCounters( event: Event ): Promise<void> {
        if ( !this._keepCount ) {
            return;
        }

        this._currentCountEventTracker = event.timeStamp.toFixed( 0 );

        if ( this._countAborter !== null ) {
            this._countAborter.abort();
            this._countAborter = null;
        }

        this._countAborter = new AbortController();

        let response: Response;

        try {
            const url = this._keepCountEndpoint;
            url.searchParams.set( 'ets', this._currentCountEventTracker );

            response = await window.http( this._keepCountEndpoint, {
                method: 'POST',
                body: new FormData( this._form ),
                signal: this._countAborter.signal,
            } ) as Response;
        } catch {
            return;
        }

        const responseURL = new URL( response.url );

        if ( !response.ok || responseURL.searchParams.get( 'ets' ) !== this._currentCountEventTracker ) {
            return;
        }

        const data: CountResponse = await response.json();

        this._form.dataset.locationCount = data.locationCount.toString();
        this._form.dataset.propertyCount = data.propertyCount.toString();

        const filterCompatibilityInput = this._form.querySelector<HTMLInputElement>( 'input[name=fypFilterIsModeInterchangable]' );

        if ( filterCompatibilityInput ) {
            filterCompatibilityInput.value = data.isFYPEasyModeCompatible.toString();
        }

        this._updateCounterElements( data );
    }

    _updateCounterElements( data: CountResponse ): void {
        let btnsToFlash: Array<HTMLButtonElement> = [];

        if ( data.locationCount !== this._locationCounters[ 0 ].currentValue ) {
            btnsToFlash = [
                ...btnsToFlash,
                ...Array.from( this._locationSubmitBtns ),
            ];
        }

        if ( data.propertyCount !== this._propertyCounters[ 0 ].currentValue ) {
            btnsToFlash = [
                ...btnsToFlash,
                ...Array.from( this._propertySubmitBtns ),
            ];
        }

        if ( btnsToFlash.length > 0 ) {
            btnsToFlash.forEach( ( btn: HTMLButtonElement ) => btn.classList.add( 'locationsearchcounter__btn--flash' ) );

            window.setTimeout( () => {
                btnsToFlash.forEach( ( btn: HTMLButtonElement ) => btn.classList.remove( 'locationsearchcounter__btn--flash' ) );
            }, 1500 );
        }

        this._locationCounters.forEach( ( counter ) => counter.update( data.locationCount ) );
        this._propertyCounters.forEach( ( counter ) => counter.update( data.propertyCount ) );

        const noPropertiesMsg = document.querySelector<HTMLElement>( '#no-properties-msg' );

        if ( !noPropertiesMsg ) {
            return;
        }

        if ( data.propertyCount > 0 ) {
            noPropertiesMsg.classList.add( 'd-none' );
        } else {
            noPropertiesMsg.classList.remove( 'd-none' );
        }

        if ( data.findYourPlaceURL && data.findYourPlaceURL.length > 0 ) {
            const state = newState();
            state.title = window.document.title;
            state.url = window.location.origin + data.findYourPlaceURL;

            window.router.Replace( 'current', state );
        }
    }

    _onSimpleFilterClick( event: Event ): void {
        if ( !event.target || !( event.target instanceof HTMLButtonElement ) || !event.target.matches( '.simplelocationfilter' ) ) {
            return;
        }

        const btn = event.target as HTMLButtonElement;

        if ( btn.classList.contains( 'simplelocationfilter--enabled' ) ) {
            this._disableSimpleFilter( btn );
        } else {
            this._enableSimpleFilter( btn );
        }

        this._onChange( event );
    }

    _enableSimpleFilter( root: HTMLButtonElement ): void {
        const switchInput = root.querySelector<HTMLInputElement>( '.twc__switch input' );

        if ( switchInput ) {
            switchInput.checked = true;
        }

        const fieldset = root.querySelector<HTMLFieldSetElement>( 'fieldset' );

        if ( !fieldset ) {
            throw new Error( 'fieldset undefined' );
        }

        if ( root.id === 'location-filter-category-geography' ) {
            this._onGeoSimpleSelectChange();
        }

        fieldset.disabled = false;

        root.classList.add( 'simplelocationfilter--enabled' );
    }

    _disableSimpleFilter( root: HTMLButtonElement ): void {
        const switchInput = root.querySelector<HTMLInputElement>( '.twc__switch input' );

        if ( switchInput ) {
            switchInput.checked = false;
        }

        const fieldset = root.querySelector<HTMLFieldSetElement>( 'fieldset' );

        if ( !fieldset ) {
            throw new Error( 'fieldset undefined' );
        }

        if ( root.id === 'location-filter-category-geography' ) {
            [
                'locationId',
                'locationFreeText',
            ].forEach( ( name: string ) => {
                const input = this._form.querySelector<HTMLInputElement>( `input[name="${name}"]` );

                if ( !input ) {
                    return;
                }

                input.value = input.dataset.defaultValue || '';
            } );
        }

        fieldset.disabled = true;

        root.classList.remove( 'simplelocationfilter--enabled' );
    }

    _onFilterSectionShow( event: CustomEvent ): void {
        if ( !event.target || !( event.target instanceof HTMLElement ) ) {
            return;
        }

        const parentContainer = event.target.closest( '.locationsearchfiltercategory' );

        if ( !parentContainer ) {
            return;
        }

        const headerSwitchInput = parentContainer.querySelector<HTMLInputElement>( '.locationsearchfiltercategory__header .twc__switch input' );

        if ( headerSwitchInput ) {
            headerSwitchInput.checked = true;
        }

        const innerFieldset = parentContainer.querySelector<HTMLFieldSetElement>( 'fieldset.locationsearchfiltercategory__main' );

        if ( innerFieldset ) {
            innerFieldset.disabled = false;
        }

        this._onChange( event );
    }

    _onFilterSectionHide( event: CustomEvent ): void {
        if ( !event.target || !( event.target instanceof HTMLElement ) ) {
            return;
        }

        const parentContainer = event.target.closest( '.locationsearchfiltercategory' );

        if ( !parentContainer ) {
            return;
        }

        const headerSwitchInput = parentContainer.querySelector<HTMLInputElement>( '.locationsearchfiltercategory__header .twc__switch input' );

        if ( headerSwitchInput ) {
            headerSwitchInput.checked = false;
        }

        const innerFieldset = parentContainer.querySelector<HTMLFieldSetElement>( 'fieldset.locationsearchfiltercategory__main' );

        if ( innerFieldset ) {
            innerFieldset.disabled = true;
        }

        this._onChange( event );
    }

    _doFilterCountBadgeUpdate(): void {
        let propertyFiltersToIgnore = locationFilters.all();

        const geographyCategory = this._form.querySelector<HTMLFieldSetElement>( '#location-filter-category-geography fieldset' );

        if ( geographyCategory && geographyCategory.disabled ) {
            propertyFiltersToIgnore = [
                ...propertyFiltersToIgnore,
                'locationId',
            ];
        }

        const filterCounts = countFilters( this._form, propertyFiltersToIgnore );

        updateFilterCountBadges( this._form, filterCounts.location.count, filterCounts.property.count );
    }
}

export default LocationSearchForm;
